import React, {useEffect, useRef, useState} from 'react';
import './FindingsRemarks.scss';
import UserAvatar from '../../../../components/avatar/UserAvatar';
import {getLanguage, t} from 'react-switch-lang';
import Button from 'reactstrap/lib/Button';
import {useUserContext} from '../../../../contexts/UserContext';
import allowedFileTypes from '../../../../utils/allowedFileTypes';
import {PaperClipOutlined} from '@ant-design/icons';
import Trash from '../../../IncurredRisk/img/Trash.svg';
import {addRemark} from '../../../../services/findings';
import FindingCommentsList from './components/FindingCommentsList';
import Toast from '../../../../components/toast/Toast';
import Swal from 'sweetalert2';
import {useParams} from 'react-router-dom';
import {Tooltip} from 'antd';
import InputDate from '../../../../components/FormFields/inputDate/InputDate';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';
import Textarea from '../../../../components/FormFields/Textarea';
import SelectList from '../../../../components/select/SelectList';
import {FINDING_STATUS, PROGRESS_PERCENTAGE} from '../../const';
import roles from '../../../../utils/roles';
import {format} from 'date-fns';

const FindingsRemarks = ({findingData}) => {
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [commentingIsDisabled, setCommentingIsDisabled] = useState(false);
    const [allow, setAllow] = useState(false);
    const [resetList, setResetList] = useState(true);
    const [isProgressDisabled, setIsProgressDisabled] = useState(false);
    const [isStatusDisabled, setIsStatusDisabled] = useState(false);
    const user = useUserContext();
    const file = useRef();
    const proofFile = useRef();
    const [files, setFiles] = useState([]);
    const [proofFiles, setProofFiles] = useState([])
    const params = useParams();
    const [schema, setSchema] = useState(yup.object().shape({
        deadline: yup.date().nullable(), progressPercentage: yup
            .object()
            .nullable(), findingStatus: yup
            .object()
            .nullable(), comment: yup
            .string()
            .trim()
            .required(t('message.fieldRequired'))
            .min(3, t('message.min3'))
            .max(1000, t('message.max1000')), proofComment: yup
            .string()
            .trim().when('progressPercentage', {
                is: (value) => value?.label === '100%',
                then: (schema) => schema.required(t('message.fieldRequired'))
            })
            .max(1000, t('message.max1000')),
    }));
    const methods = useForm({
        resolver: yupResolver(schema),
    });
    const {handleSubmit, setValue, watch} = methods;

    const previewForm = findingData?.data?.responsiblePerson?.id === user?.id
    const allowRoles = [roles.role_head_of_internal_audit, roles.role_internal_auditor]
    const progressPercentage = watch('progressPercentage');
    const findingStatus = watch('findingStatus')
    const isUpdatingRef = useRef(false);

    useEffect(() => {
        if (isUpdatingRef.current) {
            isUpdatingRef.current = false;
            return;
        }

        if (progressPercentage?.label === '100%') {
            setIsStatusDisabled(true);
            isUpdatingRef.current = true;
            setValue('findingStatus', {
                label: FINDING_STATUS.COMPLETED[getLanguage() === 'eng' ? 'en' : getLanguage()],
                value: FINDING_STATUS.COMPLETED.value
            });
        } else if (findingStatus?.value === 'COMPLETED') {
            setIsStatusDisabled(false);
            isUpdatingRef.current = true;
            setValue('findingStatus', null);
        }
    }, [progressPercentage]);

    useEffect(() => {
        if (isUpdatingRef.current) {
            isUpdatingRef.current = false;
            return;
        }

        if (findingStatus?.value === 'COMPLETED' && progressPercentage?.label !== '100%') {
            setIsProgressDisabled(true);
            isUpdatingRef.current = true;
            setValue('progressPercentage', {
                label: PROGRESS_PERCENTAGE?.HUNDRED?.label, value: PROGRESS_PERCENTAGE?.HUNDRED?.value
            });
        } else {
            // setValue('progressPercentage', null);
            setIsProgressDisabled(false);
        }
    }, [findingStatus]);


    useEffect(() => {
        if (findingData?.data?.findingStatus === FINDING_STATUS.ABORTED.value || findingData?.data?.findingStatus === FINDING_STATUS.COMPLETED.value) {
            setCommentingIsDisabled(true);
        }
    }, [findingData]);

    useEffect(() => {
        const result = allowRoles.filter((element) => user?.authorities?.includes(element));
        setAllow(result.length > 0);
    }, [user]);

    const addComment = async (data) => {
        setIsButtonDisabled(true);
        const formData = new FormData();
        let finalDeadline = null;

        if (data.deadline) {
            finalDeadline = format(new Date(data.deadline), 'yyyy-MM-dd') + 'T' + format(new Date(data.deadline), 'hh:mm:ss');
        }

        formData.append('remarkDTO', JSON.stringify({
            content: data?.comment,
            findingCompletionProof: data?.proofComment,
            progressPercentage: data?.progressPercentage?.value,
            responsiblePersonFindingStatus: data?.findingStatus?.value,
            findingId: params.findingId,
            proposedDeadline: finalDeadline,
        }));
        if (files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                formData.append('files', files[i]);
            }
        }
        if (proofFiles.length > 0) {
            formData.append('findingCompletionProofFile', proofFiles[0]);
        }
        addRemark(formData)
            .then((r) => {
                Toast.fire({
                    icon: 'success', title: `${t('message.successAdd')}`,
                });
                setFiles([]);
                setProofFiles([]);
                setValue('comment', '');
                setValue('progressPercentage', '');
                setValue('findingStatus', '');
                setValue('deadline', null);
                setIsButtonDisabled(false);
                setIsStatusDisabled(false);
                setIsProgressDisabled(false)
                setResetList((prevState) => !prevState);
            })
            .catch((err) => {
                if (err.response.data.code === 'finding-invalid-deadline-date') {
                    Swal.fire({
                        text: `${t('revisions.findingInvalidProposedDeadlineDate')}`,
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        text: `${t('message.errorApi')}`, icon: 'error', confirmButtonText: 'OK',
                    });
                }

                setIsButtonDisabled(false);
            });
    };
    // const uploadOnChange = () => {
    //     const input = document.getElementById('upload');
    //     if (input?.files[0]?.size > 10485760) {
    //         Toast.fire({
    //             title: t('message.fileTooBig'), icon: 'error',
    //         });
    //         setFiles([]);
    //     } else {
    //         setFiles(input?.files)
    //     }
    // }
    const uploadOnChange = () => {
        const input = document.getElementById('upload');
        if (!input?.files) return;

        const maxSize = 10485760; // 10MB
        const maxTotalSize = 52428800; // 50MB total
        const selectedFiles = Array.from(input.files);

        const validFiles = selectedFiles.filter(file => {
            if (file.size > maxSize) {
                Toast.fire({
                    title: t('message.fileTooBig', {fileName: file.name}), icon: 'error',
                });
                return false;
            }
            return true;
        });

        setFiles(prevFiles => {
            const totalSize = [...prevFiles, ...validFiles].reduce((acc, file) => acc + file.size, 0);

            if (totalSize > maxTotalSize) {
                Toast.fire({
                    title: t('message.totalFileSizeExceeded'),
                    icon: 'error',
                });
                return prevFiles;
            }

            return [...prevFiles, ...validFiles];
        });

        // setFiles(prevFiles => [...prevFiles, ...validFiles]);
    };

    const proofFilesUploadChange = () => {
        const input = document.getElementById('proofUpload');
        if (input?.files[0]?.size > 10485760) {
            Toast.fire({
                title: t('message.fileTooBig'), icon: 'error',
            });
            setProofFiles([]);
        } else {
            setProofFiles(input?.files)
        }
    }

    return (<>
        <FindingCommentsList resetList={resetList}/>
        {(previewForm || allow) && <div className="findings-remarks-add-document-comment-footer">
            <div className="findings-remarks-add-document-comment">
                <FormProvider {...methods}>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <label style={{marginLeft: '38px'}} className="label-referentId">{t('comments.comment')}</label>
                        <div style={{display: 'flex'}}>
                            <div className="findings-remarks-add-document-comment__image">
                                <UserAvatar user={user}/>
                            </div>
                            <form
                                style={{minHeight: '108px'}}
                                className="findings-remarks-add-document-comment__field"
                                onSubmit={handleSubmit(addComment)}
                            >
                                <Textarea
                                    placeholder={t('comments.leaveComment')}
                                    name="comment"
                                    id="comment"
                                    disabled={commentingIsDisabled}
                                />

                                {!commentingIsDisabled && (<>
                                    <div
                                        style={{position: 'relative', right: progressPercentage?.label === '100%' ? '20px' : '120px',
                                                top:  progressPercentage?.label === '100%' ? '-10px' : '13px'}}
                                    >
                                        <>
                                            <PaperClipOutlined
                                                onClick={() => {
                                                    file.current.click();
                                                }}
                                                style={{fontSize: '22px', color: '#08c',position:'absolute',bottom: '0',right: '0'}}
                                            />
                                            <input
                                                style={{display: 'none'}}
                                                id="upload"
                                                type="file"
                                                multiple={true}
                                                name="file"
                                                ref={file}
                                                onChange={() => {
                                                    uploadOnChange()
                                                }}
                                                accept={allowedFileTypes}
                                            />
                                        </>  <div style={{marginRight: '30px'}}>{(files.map((file) => (<div
                                                className="d-flex align-items-center findings-remarks-file-attachment">
                                                <Tooltip title={file?.name}>
                                                    <span className="mr-5">{file?.name}</span>
                                                </Tooltip>

                                                <img
                                                    src={Trash}
                                                    alt="trash"
                                                    style={{cursor: 'pointer'}}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setFiles(prevFiles => prevFiles.filter(f => !(f.name === file.name && f.size === file.size)));
                                                    }}
                                                />
                                    </div>)))}</div>
                                    </div>

                                    {progressPercentage?.label !== '100%'  && <Button
                                        type="submit"
                                        className="add-comment-button"
                                        disabled={isButtonDisabled}
                                    >
                                        {t('comments.submitComment')}
                                    </Button>}
                                </>)}
                            </form>
                        </div>

                        {progressPercentage?.label === '100%' && <><label
                            style={{marginLeft: '35px', marginTop: '45px'}}
                            className="label-referentId">{t('comments.proofOfRealization')}</label>
                            <div style={{display: 'flex'}}>
                                <div className="findings-remarks-add-document-comment__image">
                                    <UserAvatar user={user}/>
                                </div>
                                <div style={{position: 'relative'}}>
                                <form
                                    style={{height: '108px'}}
                                    className="findings-remarks-add-document-comment__field"
                                    onSubmit={handleSubmit(addComment)}
                                >
                                    <Textarea
                                        placeholder={t('comments.leaveComment')}
                                        name="proofComment"
                                        id="proofComment"
                                        disabled={commentingIsDisabled}
                                    />


                                    {!commentingIsDisabled && (<>
                                        <div
                                            style={{position: 'relative', right: '20px', top: '-10px'}}
                                        >
                                            {proofFiles.length === 0 ? (<>
                                                <PaperClipOutlined
                                                    onClick={() => {
                                                        proofFile.current.click();
                                                    }}
                                                    style={{fontSize: '22px', color: '#08c'}}
                                                />
                                                <input
                                                    style={{display: 'none'}}
                                                    id="proofUpload"
                                                    type="file"
                                                    name="file"
                                                    ref={proofFile}
                                                    onChange={() => {
                                                        proofFilesUploadChange()
                                                    }}
                                                    accept={allowedFileTypes}
                                                />
                                            </>) : (<div
                                                className="d-flex align-items-center findings-remarks-file-attachment">
                                                <Tooltip title={proofFiles[0]?.name}>
                                                    <span className="mr-5">{proofFiles[0]?.name}</span>
                                                </Tooltip>

                                                <img
                                                    src={Trash}
                                                    alt="trash"
                                                    style={{cursor: 'pointer'}}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setProofFiles([]);
                                                    }}
                                                />
                                            </div>)}
                                        </div>
                                    </>)}
                                </form>
                                {progressPercentage?.label === '100%' &&
                                    <Button
                                        type="submit"
                                        className="add-comment-button"
                                        disabled={isButtonDisabled}
                                        style={{marginTop: 'auto', marginLeft: 'auto', width: 'fit-content',
                                            backgroundColor: '#007dc3',border: '0',position: 'absolute',right: '-110px',bottom: '1px'}}
                                        onClick={handleSubmit(addComment)}
                                    >
                                        {t('comments.submitComment')}
                                    </Button>}
                            </div>
                            </div>
                        </>}
                    </div>
                    <div
                        className={'progress-date-wrapper'}
                    >
                        {previewForm && <>  <label style={{margin: '0'}} className="label-referentId">
                            {t('revisions.proposedDeadline')}
                        </label>
                            <InputDate
                                isClearable={true}
                                disabled={commentingIsDisabled}
                                name={'deadline'}
                            />
                        </>}
                        {previewForm && <>
                            <label style={{margin: '0', paddingTop: '10px'}} className="label-referentId">
                                {t('revisions.progressPercentage')}
                            </label>
                            <SelectList
                                name="progressPercentage"
                                className="select-field"
                                placeholder={t('revisions.placeholder.progressPercentage')}
                                options={[{
                                    label: PROGRESS_PERCENTAGE?.ZERO?.label, value: PROGRESS_PERCENTAGE?.ZERO?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.TEN?.label, value: PROGRESS_PERCENTAGE?.TEN?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.TWENTY?.label, value: PROGRESS_PERCENTAGE?.TWENTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.THIRTY?.label, value: PROGRESS_PERCENTAGE?.THIRTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.FORTY?.label, value: PROGRESS_PERCENTAGE?.FORTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.FIFTY?.label, value: PROGRESS_PERCENTAGE?.FIFTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.SIXTY?.label, value: PROGRESS_PERCENTAGE?.SIXTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.SEVENTY?.label,
                                    value: PROGRESS_PERCENTAGE?.SEVENTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.EIGHTY?.label, value: PROGRESS_PERCENTAGE?.EIGHTY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.NINETY?.label, value: PROGRESS_PERCENTAGE?.NINETY?.value
                                }, {
                                    label: PROGRESS_PERCENTAGE?.HUNDRED?.label,
                                    value: PROGRESS_PERCENTAGE?.HUNDRED?.value
                                }]}
                                isDisabled={commentingIsDisabled || isProgressDisabled}
                            />
                        </>}
                    </div>
                    {previewForm && <div
                        className={`status-wrapper ${progressPercentage?.label === '100%' && 'status-full-height'}`}
                    >
                        <label style={{margin: '0'}}>{t('revisions.referralStatus')}</label>
                        <SelectList
                            name="findingStatus"
                            className="select-field-status"
                            placeholder={t('revisions.placeholder.findingStatus')}
                            isDisabled={commentingIsDisabled || isStatusDisabled}
                            options={getLanguage() === 'eng' ? [{
                                label: FINDING_STATUS.COMPLETED.en, value: FINDING_STATUS.COMPLETED.value,
                            }, {
                                label: FINDING_STATUS.INCOMPLETE.en, value: FINDING_STATUS.NOT_STARTED.value,
                            }, {
                                label: FINDING_STATUS.IN_PROGRESS.en, value: FINDING_STATUS.IN_PROGRESS.value,
                            }] : [{
                                label: FINDING_STATUS.COMPLETED.mne, value: FINDING_STATUS.COMPLETED.value,
                            }, {
                                label: FINDING_STATUS.INCOMPLETE.mne, value: FINDING_STATUS.NOT_STARTED.value,
                            }, {
                                label: FINDING_STATUS.IN_PROGRESS.mne, value: FINDING_STATUS.IN_PROGRESS.value,
                            }]}
                        />
                    </div>}
                </FormProvider>
            </div>
        </div>}
    </>);
};

export default FindingsRemarks;
